/**
 * Shared correct / incorrect / unsure controls + inline comment for an
 * answer. Used in three places — the Q&A panel `AnswersRow`, report cells'
 * bullet-list multi-answer branch, and the graph-full "View Answer" branch.
 *
 * Treats the most recent feedback row from the *current user* as the
 * authoritative state of the controls; other users' rows show up in lists
 * elsewhere (e.g. an evidence/discussion view), not in this control.
 */

import { memo, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check, X, HelpCircle, MessageSquare, Scissors } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import { useAppContext } from "@/providers/app-context";
import {
  type AnswerFeedback,
  type FeedbackCategory,
  type FeedbackStatus,
  FEEDBACK_CATEGORY_LABELS,
  FeedbackApiError,
  canReadOrgFeedback,
  categoriesFor,
  fetchLatestGroupVerdict,
  markFeedback,
  newClientEventId,
  retractFeedback,
  useAnswerFeedback,
} from "@/api/answer-feedback";
import { analysisKeys, type AnalysisReport } from "@/api/analyses";
import { type AnsweredQuestion } from "@/api/q-and-a";

interface Props {
  questionId: string;
  answerGroupId: string;
  /** Optional answer (DocumentAnswer) id — narrows the feedback row to a
   *  specific extraction within a group, useful when one AnswerGroup carries
   *  multiple DocumentAnswers (composites, time-series). */
  answerId?: string | null;
  /** Set when the control is rendered inside a report cell so feedback
   *  carries traceability back to the originating report. */
  reportContextId?: string | null;
  /** Layout variant. `compact` collapses controls behind a small icon row;
   *  `inline` shows pills + a comment-toggle button on one line. */
  variant?: "inline" | "compact";
  /** Optional extra class applied to the outer container. */
  className?: string;
  /** Optional live-Q&A curation hook. Reports can still use feedback without
   *  exposing answer-group mutation controls. */
  onCurateOut?: (reason?: string) => void | Promise<void>;
  curateOnIncorrect?: boolean;
  isCurating?: boolean;
}

const STATUS_OPTIONS: Array<{
  value: FeedbackStatus;
  label: string;
  Icon: typeof Check;
  activeClasses: string;
  idleClasses: string;
}> = [
  {
    value: "correct",
    label: "Correct",
    Icon: Check,
    activeClasses: "bg-emerald-100 text-emerald-800 border-emerald-200",
    idleClasses: "text-gray-500 hover:bg-emerald-50 hover:text-emerald-700",
  },
  {
    value: "incorrect",
    label: "Incorrect",
    Icon: X,
    activeClasses: "bg-rose-100 text-rose-800 border-rose-200",
    idleClasses: "text-gray-500 hover:bg-rose-50 hover:text-rose-700",
  },
  {
    value: "unsure",
    label: "Unsure",
    Icon: HelpCircle,
    activeClasses: "bg-amber-100 text-amber-800 border-amber-200",
    idleClasses: "text-gray-500 hover:bg-amber-50 hover:text-amber-700",
  },
];

/** Pick the most recent value-target row authored by the current user, if
 *  any. Server returns a flat list ordered by created_at desc.
 *
 *  Defensive: skips falsy entries so a stale react-query cache or a
 *  bad splice can't crash the render. Without this, one undefined row
 *  in the cache (which can sneak in from a setQueriesData call before
 *  the new BE endpoint shape was loaded) propagates as a hard render
 *  error and the whole component tree boundary-fails. */
function pickOwnLatest(
  rows: AnswerFeedback[] | undefined,
  currentUserEmail: string | null | undefined,
  answerId?: string | null,
): AnswerFeedback | undefined {
  if (!rows || !currentUserEmail) return undefined;
  // Match the specific answer this control represents: the BE canonical
  // identity includes `answer`, so a multi-answer group has distinct rows
  // per answer. answerId absent → match the group-level (answer=null) row.
  return rows.find(
    (r) =>
      r &&
      r.feedback_target === "value" &&
      r.user_email === currentUserEmail &&
      (r.answer ?? null) === (answerId ?? null),
  );
}

function AnswerFeedbackControlsImpl({
  questionId,
  answerGroupId,
  answerId,
  reportContextId,
  variant = "inline",
  className = "",
  onCurateOut,
  curateOnIncorrect = false,
  isCurating = false,
}: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const currentUserEmail = useCurrentUserEmail();
  const { actingOrganization } = useAppContext();

  const { data: feedback } = useAnswerFeedback({
    questionId,
    answerGroupId,
    target: "value",
    mineOnly: true,
  });
  const own = pickOwnLatest(feedback, currentUserEmail, answerId);

  const [pendingStatus, setPendingStatus] = useState<FeedbackStatus | null>(
    null,
  );
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [category, setCategory] = useState<FeedbackCategory | "">("");
  const [isSaving, setIsSaving] = useState(false);

  // One idempotency key per logical save action: minted lazily on first
  // submit, reused across retries of the SAME content (so the BE dedups
  // and surfaces a 409 on a true conflict), and reset whenever the content
  // changes or a save succeeds.
  const clientEventIdRef = useRef<string | null>(null);
  const resetClientEventId = () => {
    clientEventIdRef.current = null;
  };

  // Matches every cached useAnswerFeedback query for this AnswerGroup (any
  // target / mine flag) so cache splices + removals hit all views that
  // surface it. Key: ["answer-feedback","question",qId,group,target,(mine|all)].
  const matchesThisGroup = (key: readonly unknown[]): boolean => {
    if (key[0] !== "answer-feedback" || key[1] !== "question") return false;
    if (key[3] !== answerGroupId) return false;
    const keyTarget = key[4];
    return keyTarget === null || keyTarget === "value";
  };

  // THE freshness mechanism for `latest_feedback_status`: writes the
  // org-wide review verdict into the payload caches that carry it
  // (Answers tab list + report detail) after a mark / retract, so the
  // badge and review-status filters re-bucket instantly with no refetch,
  // on every surface, and the update survives tab switches because it
  // lives in the React Query cache, not component state. Caches that don't
  // contain this AnswerGroup keep their original reference so their
  // observers don't re-render; within a matched cache only the affected
  // entries are cloned.
  const spliceLatestFeedbackStatus = (status: FeedbackStatus | null) => {
    // own/none tiers: the server gates latest_feedback_status to null for
    // this user, so splicing a value would flash the badge/filter state
    // until the next refetch nulls it again. Skip — the payloads carry
    // nothing to keep fresh.
    if (!canReadOrgFeedback(actingOrganization)) return;
    const groupMatches = (g: { id: string }) => g.id === answerGroupId;
    queryClient.setQueriesData<AnsweredQuestion[]>(
      { predicate: (q) => q.queryKey[0] === "answers" },
      (old) => {
        if (!old?.some((qn) => (qn.answer_groups ?? []).some(groupMatches))) {
          return old;
        }
        return old.map((qn) =>
          (qn.answer_groups ?? []).some(groupMatches)
            ? {
                ...qn,
                answer_groups: qn.answer_groups.map((g) =>
                  groupMatches(g)
                    ? { ...g, latest_feedback_status: status }
                    : g,
                ),
              }
            : qn,
        );
      },
    );
    queryClient.setQueriesData<AnalysisReport>(
      { predicate: (q) => analysisKeys.isReportsKey(q.queryKey) },
      (old) => {
        // The key family also covers report-list/status caches; only the
        // detail payload carries question_map.
        if (!old?.question_map) return old;
        const answerMatches = (a: { answer_group_id: string | null }) =>
          a.answer_group_id === answerGroupId;
        const entries = Object.entries(old.question_map);
        if (!entries.some(([, qn]) => (qn.answers ?? []).some(answerMatches))) {
          return old;
        }
        return {
          ...old,
          question_map: Object.fromEntries(
            entries.map(([qid, qn]) =>
              (qn.answers ?? []).some(answerMatches)
                ? [
                    qid,
                    {
                      ...qn,
                      answers: qn.answers.map((a) =>
                        answerMatches(a)
                          ? { ...a, latest_feedback_status: status }
                          : a,
                      ),
                    },
                  ]
                : [qid, qn],
            ),
          ),
        };
      },
    );
  };

  // "Undo": re-clicking the already-selected verdict retracts the row, so a
  // user can remove their feedback (requirement: users can delete feedback),
  // not just change it. Optimistically drops it from the cache for this group.
  const undo = async () => {
    if (!own) return;
    setIsSaving(true);
    try {
      await retractFeedback(own.id);
      queryClient.setQueriesData<AnswerFeedback[]>(
        { predicate: (q) => matchesThisGroup(q.queryKey) },
        (old) =>
          (old ?? []).filter(
            (r): r is AnswerFeedback => Boolean(r) && r.id !== own.id,
          ),
      );
      // Re-derive the group's org-wide verdict instead of assuming
      // Unreviewed — a teammate's remaining feedback may now be the
      // latest, and splicing null would mis-bucket the answer in the
      // review-status filters until the next natural fetch.
      try {
        spliceLatestFeedbackStatus(
          await fetchLatestGroupVerdict(questionId, answerGroupId),
        );
      } catch {
        spliceLatestFeedbackStatus(null);
      }
      setPendingStatus(null);
      setComment("");
      setCategory("");
      resetClientEventId();
    } catch (e) {
      toast({
        title: "Couldn't remove feedback",
        description: e instanceof Error ? e.message : "Try again in a moment.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Sync local state from server only when the popover is closed —
  // while the user is editing, server refetches (triggered by post-
  // submit invalidation OR by sibling components polling) must not
  // overwrite their in-progress comment / category. Re-renders during
  // an open popover also destabilise the Save button (Radix repositions
  // on parent re-render). Reduce deps to `own.id + popoverOpen` so the
  // sync happens once per row, not on every server-side change.
  useEffect(() => {
    if (own && !popoverOpen) {
      setComment(own.comment);
      setCategory(own.category ?? "");
    }
  }, [own?.id, popoverOpen]);

  // Once the refetched server row catches up to a pending status, drop the
  // pending marker. Until then `activeStatus` keeps reading `pendingStatus`
  // so the controls render consistently across the brief POST→refetch race
  // — and crucially so a follow-up Save click doesn't bail with "pick a
  // status first" while the refetch is still in flight.
  useEffect(() => {
    if (pendingStatus && own?.status === pendingStatus) {
      setPendingStatus(null);
    }
  }, [pendingStatus, own?.status]);

  const activeStatus: FeedbackStatus | null =
    pendingStatus ?? own?.status ?? null;
  // In the doc viewer, marking Incorrect curates this document's answer out, so
  // the comment box + Save button speak the language of removal, not feedback.
  const isCurateRemove = curateOnIncorrect && activeStatus === "incorrect";

  const submit = async (
    status: FeedbackStatus,
    commentText: string,
    categoryValue: FeedbackCategory | "",
  ): Promise<boolean> => {
    setIsSaving(true);
    try {
      if (!clientEventIdRef.current) {
        clientEventIdRef.current = newClientEventId();
      }
      const newRow = await markFeedback(questionId, {
        feedback_target: "value",
        answer_group: answerGroupId,
        answer: answerId ?? undefined,
        status,
        comment: commentText,
        category: categoryValue || null,
        report_context: reportContextId ?? undefined,
        client_event_id: clientEventIdRef.current,
      });
      // Direct cache update across every view that surfaces this
      // AnswerGroup. The Q&A panel uses `questionId=question.question_id`
      // while a report cell uses `questionId=source_question_id` — they
      // can differ for the same AnswerGroup, so namespace-wide
      // invalidation would refetch every row's query (causing Radix
      // popover instability under polling) AND surgical invalidation
      // would miss the alternate view (causing stale "no feedback"
      // pills in the other tab — and a user could overwrite their
      // own feedback because the UI lied about persisted state).
      //
      // setQueriesData with a predicate matches every cached
      // useAnswerFeedback that filters on this answerGroupId, splices
      // the new row in, and skips the network entirely. Cross-view
      // shows the latest state immediately; no extra refetches; no
      // popover-instability tradeoff. This replaces the prior
      // surgical-invalidate path.
      // Guard against an undefined/null POST result corrupting the
      // cache — only splice if we got a real row back. apiClient
      // throws on non-2xx, so reaching this code path means a 200/201
      // body, but defense-in-depth here keeps a future BE shape change
      // from poisoning every query that matches the predicate.
      if (newRow && newRow.id) {
        queryClient.setQueriesData<AnswerFeedback[]>(
          { predicate: (q) => matchesThisGroup(q.queryKey) },
          (old) => {
            // Filter out any stale `undefined` entries the cache may
            // already carry from earlier bugs / cross-version state.
            const list = (old ?? []).filter((r): r is AnswerFeedback => Boolean(r));
            const idx = list.findIndex((r) => r.id === newRow.id);
            if (idx >= 0) {
              const next = [...list];
              next[idx] = newRow;
              return next;
            }
            // Server returns rows newest-first; prepend so pickOwnLatest
            // resolves to this row immediately.
            return [newRow, ...list];
          },
        );
      }
      spliceLatestFeedbackStatus(status);
      // Don't clear pendingStatus here — the useEffect above does it once
      // `own.status` matches, which is the only guarantee that
      // `activeStatus` stays well-defined for the next interaction.
      // This action persisted; the next save mints a fresh key.
      resetClientEventId();
      return true;
    } catch (e) {
      // Roll back the optimistic state — otherwise the failed status
      // keeps rendering as active even though it never persisted. Keep the
      // client_event_id so an immediate retry of the same content dedups.
      setPendingStatus(null);
      const is409 = e instanceof FeedbackApiError && e.status === 409;
      toast({
        title: is409 ? "Feedback changed elsewhere" : "Couldn't save feedback",
        description: is409
          ? "This was updated in another tab or request. Reopen to see the latest."
          : e instanceof Error
            ? e.message
            : "Try again in a moment.",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const onPick = (status: FeedbackStatus) => {
    // Re-clicking the already-selected verdict toggles it off: retract the
    // feedback (undo), so a user can remove a verdict, not just change it.
    if (own && own.status === status) {
      void undo();
      return;
    }
    // Flipping the verdict starts a NEW logical action, so the previous
    // verdict's rationale must not carry over: a "why it's correct"
    // comment pre-filled under "What's the reason?" is one click away
    // from being saved as the reason it's WRONG. Reset BOTH fields —
    // validity-checking the category isn't enough because the lists
    // overlap across verdicts ("other" is in every list; incorrect and
    // unsure share the whole negative list). Reopening the SAME verdict
    // (the comment button's "Edit comment" path) keeps the prefill.
    const previousStatus = pendingStatus ?? own?.status ?? null;
    if (previousStatus !== null && previousStatus !== status) {
      setComment("");
      setCategory("");
    }

    // New verdict = new logical action → new idempotency key.
    resetClientEventId();

    // Optimistic UI only — the pill flips to the chosen color via
    // pendingStatus. NO POST yet. The user types the rationale (or
    // not) and clicks Save in the popover; that's the single POST
    // for this logical action. If they dismiss the popover without
    // saving, pendingStatus is cleared (see Popover onOpenChange) and
    // no row is created.
    //
    // The earlier "auto-save on pill click" behaviour produced two
    // rows per logical action (verdict-only on click, then
    // verdict+comment on save), which inflated row counts in the admin
    // and complicated Phase 3 clustering. One POST per logical user
    // action keeps the data clean. The trade-off — a user who clicks
    // a pill and walks away without typing leaves no record — is
    // acceptable: that signal was always weak (could be exploration,
    // mis-click, distraction).
    setPendingStatus(status);
    setPopoverOpen(true);
  };

  const onSaveComment = async () => {
    const status = activeStatus;
    if (!status) {
      toast({
        title: "Pick correct, incorrect, or unsure first",
        description: "A comment alone isn't enough to save feedback.",
      });
      return;
    }
    let ok: boolean;
    if (
      curateOnIncorrect &&
      status === "incorrect" &&
      onCurateOut &&
      answerId
    ) {
      setIsSaving(true);
      try {
        await onCurateOut(comment.trim() || undefined);
        setPendingStatus(null);
        resetClientEventId();
        ok = true;
      } catch (e) {
        setPendingStatus(null);
        toast({
          title: "Couldn't remove answer",
          description: e instanceof Error ? e.message : "Try again in a moment.",
          variant: "destructive",
        });
        ok = false;
      } finally {
        setIsSaving(false);
      }
    } else {
      ok = await submit(status, comment, category);
    }
    // Keep the popover open on failure so the user can retry / not lose
    // their text; only close on a confirmed save.
    if (ok) setPopoverOpen(false);
  };

  // Outer ring color reflects the persisted feedback status so the
  // user can scan reviewed rows at a glance. Soft tints to avoid
  // competing with existing cell backgrounds. Padding is unconditional
  // (rounded-md + px-1 py-0.5 always applied) so the container's
  // height doesn't change when activeStatus flips null → set on the
  // first click. Without this, the Q&A row reflows mid-interaction,
  // drifts the popover trigger, and Radix re-anchors the popover
  // content — Save button becomes "not stable" mid-click.
  const ringClass =
    activeStatus === "correct"
      ? "ring-1 ring-emerald-200/70"
      : activeStatus === "incorrect"
        ? "ring-1 ring-rose-200/70"
        : activeStatus === "unsure"
          ? "ring-1 ring-amber-200/70"
          : "";

  return (
    <div
      className={`flex items-center gap-1 rounded-md px-1 py-0.5 ${ringClass} ${className}`}
    >
      <TooltipProvider delayDuration={300}>
        {STATUS_OPTIONS.map(({ value, label, Icon, activeClasses, idleClasses }) => {
          const isActive = activeStatus === value;
          // In the doc viewer, marking Incorrect curates this document's answer
          // out — make that explicit so the verdict button isn't mistaken for a
          // passive label.
          const tooltip =
            curateOnIncorrect && value === "incorrect"
              ? "Removes this document's answer from the group"
              : label;
          return (
            <Tooltip key={value}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={label}
                  aria-pressed={isActive}
                  disabled={isSaving}
                  onClick={() => onPick(value)}
                  className={
                    `inline-flex items-center justify-center rounded border transition-colors ` +
                    (variant === "compact"
                      ? `h-5 w-5 text-[11px] `
                      : `h-6 px-1.5 gap-1 text-[11px] `) +
                    (isActive
                      ? activeClasses
                      : `border-transparent ${idleClasses}`) +
                    (isSaving ? ` opacity-60 cursor-wait` : ``)
                  }
                >
                  <Icon size={12} />
                  {variant === "inline" && <span>{label}</span>}
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">{tooltip}</TooltipContent>
            </Tooltip>
          );
        })}

        {/* Comment button → opens the reasoning popover. Same data
            captured (category + textarea + save/cancel) but in a
            floating card instead of an inline expansion below the row,
            so dense report cells stay tidy when feedback is given. */}
        <Popover
          open={popoverOpen}
          onOpenChange={(next) => {
            setPopoverOpen(next);
            // Closing without saving rolls back the optimistic pill —
            // pendingStatus drove the active color while the popover
            // was open; clearing it on close lets activeStatus fall
            // back to the persisted own.status (or null if no prior
            // feedback). Save sets own.status via its POST, so the
            // sequencing means the active color stays correct in
            // both code paths: dismiss → revert; save → confirmed.
            if (!next) setPendingStatus(null);
          }}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  aria-label={comment ? "Edit comment" : "Add comment"}
                  className={
                    `inline-flex items-center justify-center rounded border transition-colors ` +
                    (variant === "compact"
                      ? `h-5 w-5 text-[11px] `
                      : `h-6 px-1.5 gap-1 text-[11px] `) +
                    (comment
                      ? `bg-blue-50 text-blue-700 border-blue-200`
                      : `border-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-800`)
                  }
                >
                  <MessageSquare size={12} />
                  {variant === "inline" && (
                    <span>{comment ? "Edit comment" : "Comment"}</span>
                  )}
                </button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="top">
              {comment ? "Edit comment" : "Add comment"}
            </TooltipContent>
          </Tooltip>
          <PopoverContent
            align="start"
            sideOffset={6}
            className="w-80 p-3 flex flex-col gap-2"
          >
            <div className="flex items-center justify-between">
              <h5 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                {activeStatus === "correct"
                  ? "What's working?"
                  : activeStatus
                    ? "What's the reason?"
                    : "Add a reason"}
              </h5>
              <button
                type="button"
                onClick={() => setPopoverOpen(false)}
                aria-label="Close"
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={14} />
              </button>
            </div>

            {/* Typed reason — drives Phase 3 clustering. The option
                list swaps based on status (affirmative for `correct`,
                negative for incorrect/unsure). Optional. */}
            {activeStatus && (
              <select
                value={category}
                onChange={(e) => {
                  setCategory(e.target.value as FeedbackCategory | "");
                  resetClientEventId();
                }}
                className="text-xs rounded border border-gray-200 bg-white px-2 py-1 text-gray-700 hover:border-gray-300 focus:border-blue-300 focus:outline-none focus:ring-1 focus:ring-blue-200"
              >
                <option value="">
                  {activeStatus === "correct"
                    ? "What's working? (optional)"
                    : "What's the reason? (optional)"}
                </option>
                {categoriesFor(activeStatus).map((c) => (
                  <option key={c} value={c}>
                    {FEEDBACK_CATEGORY_LABELS[c]}
                  </option>
                ))}
              </select>
            )}

            <Textarea
              aria-label="Feedback comment"
              value={comment}
              onChange={(e) => {
                setComment(e.target.value);
                resetClientEventId();
              }}
              onKeyDown={(e) => {
                // Cmd+Enter (mac) / Ctrl+Enter (win/linux) saves.
                // Plain Enter inserts a newline (multi-line ok).
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  if (!isSaving) onSaveComment();
                }
              }}
              placeholder={
                isCurateRemove
                  ? "Why remove this document's answer? (optional) — ⌘/Ctrl+Enter to remove"
                  : activeStatus === "incorrect"
                    ? "Why is this wrong? (e.g. doc says X, model said Y) — ⌘/Ctrl+Enter to save"
                    : activeStatus === "correct"
                      ? "Why is this right? (optional) — ⌘/Ctrl+Enter to save"
                      : "What's unclear about this answer? — ⌘/Ctrl+Enter to save"
              }
              className="min-h-[80px] text-xs"
            />

            <div className="flex items-center justify-end gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => {
                  setComment(own?.comment ?? "");
                  setCategory(own?.category ?? "");
                  setPopoverOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                // Reserve enough width to fit "Saving…" so the button
                // doesn't grow → snap back when isSaving toggles. Cancel
                // sits next to it; the width oscillation was a visible
                // ~20px shift mid-POST.
                className="h-7 text-xs min-w-[72px]"
                disabled={isSaving}
                onClick={onSaveComment}
              >
                {isCurateRemove
                  ? isSaving
                    ? "Removing…"
                    : "Remove answer"
                  : isSaving
                    ? "Saving…"
                    : "Save"}
              </Button>
            </div>
          </PopoverContent>
        </Popover>
        {onCurateOut && answerId && !curateOnIncorrect ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="Curate out"
                disabled={isSaving || isCurating}
                onClick={() => void onCurateOut()}
                className={
                  `inline-flex items-center justify-center rounded border border-transparent text-rose-700 transition-colors hover:bg-rose-50 ` +
                  (variant === "compact"
                    ? `h-5 w-5 text-[11px] `
                    : `h-6 px-1.5 gap-1 text-[11px] `) +
                  (isCurating ? ` opacity-60 cursor-wait` : ``)
                }
              >
                <Scissors size={12} />
                {variant === "inline" && <span>Curate out</span>}
              </button>
            </TooltipTrigger>
            <TooltipContent side="top">
              Remove this document answer from the active group
            </TooltipContent>
          </Tooltip>
        ) : null}
      </TooltipProvider>
    </div>
  );
}

/** Memo-wrap so polling-driven parent re-renders (q-and-a.tsx
 *  invalidates ["answers"] every 5s while questions are pending; report
 *  cells refresh on related cache updates) don't ripple through to the
 *  popover. Without this, Radix re-anchors the popover content on every
 *  parent render, drifting the Save button and breaking click stability.
 *  Props are all primitives (UUIDs + variant string) so the default
 *  shallow compare is sufficient. */
export const AnswerFeedbackControls = memo(AnswerFeedbackControlsImpl);

/** Read the current user's email from React Query cache (populated by
 *  `useUser` on app boot). Avoids requiring callers to thread the email
 *  through every place this control is rendered. */
function useCurrentUserEmail(): string | null {
  const queryClient = useQueryClient();
  const data = queryClient.getQueryData<{ email?: string } | undefined>([
    "currentUser",
  ]);
  return data?.email ?? null;
}
